import { Component, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { Subscription } from 'rxjs';
import { ShellService } from '../shell/shell.service';
import { FeatureService } from '../services/feature.service';
import { DashboardLayoutPreset, dashboardLayoutPreset } from '../shell/dashboard-layout-presets.component';
import {
    DashboardNotification, InspectorDashboardService, ScheduledInspection, ScheduleRange
} from './inspector-dashboard.service';

type WidgetId = 'today-inspections' | 'notifications' | 'next-inspections' | 'today-route';

interface StatusView {
    label: string;
    tone: 'success' | 'progress' | 'neutral' | 'info';
}

interface Row extends ScheduledInspection {
    time: string;               // "8:30 AM" or "Anytime"
    day: string;                // "Wed, Apr 24"
    status: StatusView;
}

interface Stop {
    n: number;
    lat: number;
    lng: number;
    label: string;
}

// Default widget order: two rows of [wide, narrow], as in the design.
const DEFAULT_LAYOUT: WidgetId[] = ['today-inspections', 'notifications', 'next-inspections', 'today-route'];
const DEFERRED_WIDGETS: WidgetId[] = ['notifications'];
const NEXT_RANGES: { value: ScheduleRange, label: string }[] = [
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'this_week', label: 'This Week' },
    { value: 'next_week', label: 'Next Week' }
];

/**
 * Inspector dashboard (Inspectmite redesign, mockup index.html): Today's Inspections,
 * Notifications, Next Inspections and Today's Route. Shown to inspectors only; other roles get
 * the existing dashboard (see DashboardHomeComponent).
 *
 * Widget order, collapsed widgets, the layout preset and the Next Inspections range are per-user preferences
 * (ShellService), so they follow the inspector across devices.
 */
@Component({
    selector: 'app-inspector-dashboard',
    templateUrl: './inspector-dashboard.component.html',
    styleUrls: ['./inspector-dashboard.component.scss']
})
export class InspectorDashboardComponent implements OnInit, OnDestroy {
    layout: WidgetId[] = DEFAULT_LAYOUT.slice();
    collapsed: string[] = [];
    readonly nextRanges = NEXT_RANGES;
    readonly titles: { [id: string]: string } = {
        'today-inspections': 'Today’s Inspections',
        'notifications': 'Notifications',
        'next-inspections': 'Next Inspections',
        'today-route': 'Today’s Route'
    };
    nextRange: ScheduleRange = 'tomorrow';
    // Column split of each row; 66/33 is the design's original layout.
    preset: DashboardLayoutPreset = 'sidebar';

    today: Row[] = [];
    next: Row[] = [];
    todayState: 'loading' | 'ready' | 'error' = 'loading';
    nextState: 'loading' | 'ready' | 'error' = 'loading';
    notifications: DashboardNotification[] = [];
    stops: Stop[] = [];
    unmappedStops = 0;
    routeUrl = '';

    dragging: WidgetId = null;
    dragOver: WidgetId = null;

    private now = new Date();
    private offset = '';        // app timezone offset from the API, e.g. "-07:00"
    private subscriptions: Subscription[] = [];
    private refreshTimer: any;

    constructor(private api: InspectorDashboardService, private shell: ShellService, public features: FeatureService) { }

    ngOnInit() {
        this.subscriptions.push(this.shell.preferences$.subscribe(() => this.readPreferences()));
        this.loadToday();
        this.loadNext();
        this.subscriptions.push(this.api.notifications().subscribe(items => this.notifications = items));
        // Statuses ("In Progress") and today's list go stale if the page is left open.
        this.refreshTimer = setInterval(() => this.loadToday(), 5 * 60 * 1000);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        clearInterval(this.refreshTimer);
    }

    // ---- Layout ----

    // Rebuilt only when the layout changes: *ngFor over fresh arrays on every check would recreate
    // every widget (and the map) each time, which loops.
    rows: WidgetId[][] = [];

    private setLayout(layout: WidgetId[]) {
        this.layout = layout;
        // Notifications stay in the saved order but are not shown until the feature exists
        // (mockup data-feature-deferred); a row left with one widget spans the full width.
        this.rows = [layout.slice(0, 2), layout.slice(2, 4)].map(row => row.filter(id => DEFERRED_WIDGETS.indexOf(id) === -1));
    }

    setPreset(preset: DashboardLayoutPreset) {
        this.preset = preset;
        this.shell.set('dashboard_preset', preset);
    }

    isCollapsed(id: WidgetId): boolean {
        return this.collapsed.indexOf(id) !== -1;
    }

    toggleCollapsed(id: WidgetId) {
        this.collapsed = this.isCollapsed(id) ? this.collapsed.filter(c => c !== id) : this.collapsed.concat(id);
        this.shell.set('dashboard_collapsed', this.collapsed);
    }

    onDragStart(event: DragEvent, id: WidgetId) {
        this.dragging = id;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', id);   // Firefox needs data to start a drag
    }

    onDragOver(event: DragEvent, id: WidgetId) {
        if (this.dragging && this.dragging !== id) {
            event.preventDefault();
            this.dragOver = id;
        }
    }

    onDrop(event: DragEvent, id: WidgetId) {
        event.preventDefault();
        if (this.dragging && this.dragging !== id) {
            this.swap(this.dragging, id);
        }
        this.onDragEnd();
    }

    onDragEnd() {
        this.dragging = null;
        this.dragOver = null;
    }

    // Keyboard alternative to dragging: arrow keys on the handle move the widget one place.
    onHandleKey(event: KeyboardEvent, id: WidgetId) {
        const steps: { [key: string]: number } = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };
        const step = steps[event.key];
        if (!step) {
            return;
        }
        event.preventDefault();
        const target = this.layout[this.layout.indexOf(id) + step];
        if (target) {
            this.swap(id, target);
        }
    }

    private swap(a: WidgetId, b: WidgetId) {
        const layout = this.layout.slice();
        const i = layout.indexOf(a);
        const j = layout.indexOf(b);
        layout[i] = b;
        layout[j] = a;
        this.setLayout(layout);
        this.shell.set('dashboard_layout', layout);
    }

    private readPreferences() {
        const saved = this.shell.get('dashboard_layout', null);
        let layout = DEFAULT_LAYOUT.slice();
        if (Array.isArray(saved)) {
            // Keep only known widgets, then append any the saved layout doesn't have yet.
            const known = saved.filter((id: WidgetId) => DEFAULT_LAYOUT.indexOf(id) !== -1);
            layout = known.concat(DEFAULT_LAYOUT.filter(id => known.indexOf(id) === -1));
        }
        // Only when it actually changed, so an unrelated preference (e.g. dark mode) doesn't
        // rebuild the widgets and reload the map.
        if (!this.rows.length || layout.join() !== this.layout.join()) {
            this.setLayout(layout);
        }
        const collapsed = this.shell.get('dashboard_collapsed', []);
        this.collapsed = Array.isArray(collapsed) ? collapsed : [];
        this.preset = dashboardLayoutPreset(this.shell.get('dashboard_preset', 'sidebar'), 'sidebar');
        const range = this.shell.get('dashboard_next_range', 'tomorrow');
        if (range !== this.nextRange && NEXT_RANGES.some(r => r.value === range)) {
            this.nextRange = range;
            this.loadNext();
        }
    }

    // ---- Data ----

    setNextRange(range: ScheduleRange) {
        this.nextRange = range;
        this.shell.set('dashboard_next_range', range);
        this.loadNext();
    }

    private loadToday() {
        this.api.schedule('today').subscribe(res => {
            this.now = new Date(res.now);
            this.offset = res.now.slice(-6);
            this.today = res.inspections.map(i => this.toRow(i, true));
            this.buildRoute(res.inspections);
            this.todayState = 'ready';
        }, () => this.todayState = 'error');
    }

    private loadNext() {
        this.nextState = 'loading';
        const range = this.nextRange;
        this.api.schedule(range).subscribe(res => {
            if (range !== this.nextRange) {
                return;     // a newer range was picked while this was loading
            }
            this.offset = res.now.slice(-6);
            this.next = res.inspections.map(i => this.toRow(i, false));
            this.nextState = 'ready';
        }, () => this.nextState = 'error');
    }

    private toRow(item: ScheduledInspection, isToday: boolean): Row {
        // Format in the app timezone (from the API), not the browser's.
        const tz = this.offset.replace(':', '');
        return Object.assign({}, item, {
            time: item.anytime ? 'Anytime' : formatDate(item.scheduled_at, 'h:mm a', 'en-US', tz),
            day: formatDate(item.scheduled_at, 'EEE, MMM d', 'en-US', tz),
            status: isToday ? this.todayStatus(item) : this.upcomingStatus(item)
        });
    }

    // Derived only from what the job record says — no guessing beyond the scheduled window.
    private todayStatus(item: ScheduledInspection): StatusView {
        if (item.completed) {
            return { label: 'Completed', tone: 'success' };
        }
        if (item.anytime) {
            return { label: 'Anytime', tone: 'info' };
        }
        const start = new Date(item.scheduled_at).getTime();
        const end = item.ends_at ? new Date(item.ends_at).getTime() : null;
        const now = this.now.getTime();
        if (now < start) {
            return { label: 'Not Started', tone: 'neutral' };
        }
        if (end === null || now <= end) {
            return { label: 'In Progress', tone: 'progress' };
        }
        return { label: 'Not Completed', tone: 'neutral' };
    }

    private upcomingStatus(item: ScheduledInspection): StatusView {
        return item.anytime ? { label: 'Anytime', tone: 'info' } : { label: 'Scheduled', tone: 'info' };
    }

    // Pins for today's stops, numbered in visiting order. No directions are requested (that
    // needs Google Directions credits); "Open full route" hands the stops to Google Maps instead.
    private buildRoute(items: ScheduledInspection[]) {
        const withAddress = items.filter(i => i.address1);
        // Numbered by position in Today's Inspections, so pin 3 is row 3 even if row 2 has no map location.
        this.stops = items
            .map((i, index) => ({ n: index + 1, lat: i.lat, lng: i.lng, label: i.address1 }))
            .filter(s => s.lat !== null && s.lng !== null);
        this.unmappedStops = items.length - this.stops.length;
        this.routeUrl = '';
        if (withAddress.length) {
            const addresses = withAddress.map(i => encodeURIComponent(i.address1 + ', ' + i.address2));
            const destination = addresses[addresses.length - 1];
            const waypoints = addresses.slice(0, -1).join('%7C');
            this.routeUrl = 'https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=' + destination
                + (waypoints ? '&waypoints=' + waypoints : '');
        }
    }

    trackByJob(index: number, row: Row) {
        return row.job_id;
    }
}
